import { DiscoveryRefusal } from '../types/discovery.types';
import { formatCredits } from './credits';

/**
 * The step-2 entry credit gate: can the org's balance cover discovery for
 * every line still to be paid for? Discovery costs one rate per component and
 * is charged at the step 2 → 3 exit, so a buyer who cannot afford every line
 * is asked, on ENTERING step 2, which lines to take forward — not after they
 * have done all of step 2.
 *
 * Lines already paid for (`paidComponentIds`) are exempt: they cost nothing
 * more, so they neither count towards the cost nor towards what the balance
 * must cover. An unknown rate or balance never gates — the step cannot state a
 * shortfall it does not know about; the exit stays the safety net.
 *
 * Contract: docs/flows/02-bom-edit-and-discovery-charge.md, "Entering step 2".
 */

export interface GateLine {
    componentId?: string;
}

export interface CreditGateInput {
    components: GateLine[];
    paidComponentIds?: string[];
    /** Credits per component; null while unknown. */
    rate: number | null;
    /** Org balance; null while loading or unreachable. */
    balance: number | null;
    /** The rate or the balance is still being read (see `BomPricing.pending`). */
    pending?: boolean;
    readOnly: boolean;
}

export type CreditGate =
    /**
     * `pending`: the rate or balance is still loading and there are lines to
     * price, so the step must wait rather than open interactive and be
     * covered by the gate a moment later.
     */
    | { needed: false; pending: boolean }
    | {
          needed: true;
          pending: false;
          /** Lines on the BOM that can be picked (they carry an id). */
          total: number;
          /** Of those, the ones not yet paid for. */
          unpaid: number;
          /** Of those, the ones already paid for: they stay, outside the cap. */
          paid: number;
          /** Unpaid lines the balance covers. */
          affordable: number;
          /** What the unpaid lines cost. */
          cost: number;
          balance: number;
          rate: number;
          /** The paid-for lines that are on the BOM now. */
          paidIds: string[];
          /** Whether there is anything to pick or keep; false = the buyer can only get credits. */
          pickable: boolean;
          /** Robbie's sentence, first person. */
          message: string;
          /** The same facts in the shape the admin note and the picker already read. */
          refusal: DiscoveryRefusal;
      };

const credits = (n: number) => `${formatCredits(n)} credit${n === 1 ? '' : 's'}`;
const componentCount = (n: number) => `${formatCredits(n)} component${n === 1 ? '' : 's'}`;

interface Facts {
    total: number;
    unpaid: number;
    paid: number;
    affordable: number;
    cost: number;
    balance: number;
}

/** Robbie's sentence: what the balance covers, what to do, what happens to the rest. */
export const gateMessage = ({ total, unpaid, paid, affordable, cost, balance }: Facts): string => {
    if (affordable === 0 && paid === 0) {
        return `Your org has ${credits(balance)}, not enough for one component. Request more credits, then come back.`;
    }
    if (affordable === 0) {
        return (
            `Your org has ${credits(balance)}, not enough for one more component. ` +
            `I can take the ${paid} already paid for forward now and the rest leave this BOM, ` +
            'or you can request more credits and come back for them.'
        );
    }
    const enough = `Your org has ${formatCredits(balance)}, enough for ${formatCredits(affordable)}.`;
    if (paid === 0) {
        const pick = affordable === 1 ? 'Pick the one' : `Pick the ${formatCredits(affordable)}`;
        return (
            `This BOM has ${componentCount(total)} and discovery will cost ${credits(cost)}. ${enough} ` +
            `${pick} to take forward; the rest leave this BOM.`
        );
    }
    const stay = paid === 1 ? 'The 1 already paid for stays.' : `The ${paid} already paid for stay.`;
    const pick = affordable === 1 ? 'Pick the one new component' : `Pick the ${formatCredits(affordable)} new ones`;
    return (
        `${formatCredits(unpaid)} of this BOM's ${formatCredits(total)} components still need discovery, which will cost ${credits(cost)}. ` +
        `${enough} ${stay} ${pick} to take forward; the rest leave this BOM.`
    );
};

export const evaluateCreditGate = ({
    components: lines,
    paidComponentIds = [],
    rate,
    balance,
    pending = false,
    readOnly,
}: CreditGateInput): CreditGate => {
    if (readOnly) return { needed: false, pending: false };

    // Only a line with an id can be sent back to be kept.
    const ids = lines.flatMap((line) => (line.componentId ? [line.componentId] : []));
    const paidSet = new Set(paidComponentIds);
    const paidIds = ids.filter((id) => paidSet.has(id));
    const unpaid = ids.length - paidIds.length;
    // Nothing to price, so nothing to wait for.
    if (unpaid === 0) return { needed: false, pending: false };
    if (pending) return { needed: false, pending: true };
    if (!rate || rate < 0 || balance === null) return { needed: false, pending: false };

    const cost = unpaid * rate;
    if (cost <= balance) return { needed: false, pending: false };

    const affordable = Math.floor(balance / rate);
    const facts: Facts = { total: ids.length, unpaid, paid: paidIds.length, affordable, cost, balance };
    const message = gateMessage(facts);
    return {
        needed: true,
        pending: false,
        ...facts,
        rate,
        paidIds,
        pickable: affordable > 0 || paidIds.length > 0,
        message,
        refusal: { message, components: unpaid, cost, balance, affordable, rate },
    };
};
